import type { SignupTheme } from "../../types";
import styles from "./SignupScene.module.css";

interface SignupSceneProps {
  theme: SignupTheme;
  /** "full" fills the public signup page behind the identity mark and form card.
   *  "card" sits behind the owner's compact live-preview card.
   *  "swatch" is a small thumbnail used by the scene picker. */
  variant?: "full" | "card" | "swatch";
  className?: string;
}

type SceneVariant = NonNullable<SignupSceneProps["variant"]>;

/** Purely decorative retail backdrop, shared by the public signup page, the
 * owner's brand-kit live preview, and the scene picker's thumbnails so all
 * three stay in visual sync. Every dimension is sized relative to its own
 * container (percentages, no viewport units below the "full" variant) so the
 * same markup reads correctly whether it fills a page or a 72px swatch. */
export function SignupScene({ theme, variant = "full", className }: SignupSceneProps) {
  const classes = `${styles.scene} ${styles[variant]}${className ? ` ${className}` : ""}`;
  return (
    <div className={classes} aria-hidden="true">
      {theme === "counter" && (
        <>
          <div className={styles.counterGlow} />
          <div className={styles.counterEdge} />
        </>
      )}
      {theme === "awning" && <AwningScene variant={variant} />}
      {theme === "window" && (
        <>
          <div className={styles.windowBeam} />
          <div className={styles.windowMullions} />
          <div className={styles.windowSill} />
        </>
      )}
      {theme === "festival" && <FestivalScene variant={variant} />}
    </div>
  );
}

const SCALLOPS: Record<SceneVariant, readonly number[]> = {
  full: Array.from({ length: 14 }, (_, index) => index),
  card: Array.from({ length: 9 }, (_, index) => index),
  swatch: Array.from({ length: 6 }, (_, index) => index),
};

function AwningScene({ variant }: { variant: SceneVariant }) {
  const scallops = SCALLOPS[variant];
  return (
    <>
      <div className={styles.awningCanopy} />
      <ul className={styles.valance}>
        {scallops.map((index) => (
          <li key={index} className={index % 2 === 0 ? styles.valanceA : styles.valanceB} />
        ))}
      </ul>
      <div className={styles.awningShade} />
      <div className={styles.awningDapple} />
    </>
  );
}

/** Bulbs sag along the same quadratic curve as the wire beneath them: with
 * endpoints pinned at y=0 and a control point at y=2*MAX_DROP, the curve's
 * value at t is MAX_DROP * 4t(1-t) — exactly the bulb sag formula below. */
const MAX_DROP = 88;

const BULB_HUE_CLASSES = [styles.bulbHue0, styles.bulbHue1, styles.bulbHue2] as const;

interface Bulb {
  hue: number;
  style: {
    left: string;
    top: string;
    animationDelay: string;
  };
}

function buildBulbs(count: number): Bulb[] {
  return Array.from({ length: count }, (_, index) => {
    const t = count === 1 ? 0.5 : index / (count - 1);
    const sag = 4 * t * (1 - t);
    return {
      hue: index % 3,
      style: {
        left: `${t * 100}%`,
        top: `${sag * MAX_DROP}%`,
        animationDelay: `${(index * 0.35) % 2.4}s`,
      },
    };
  });
}

const BULBS: Record<SceneVariant, readonly Bulb[]> = {
  full: buildBulbs(9),
  card: buildBulbs(7),
  swatch: buildBulbs(5),
};

function FestivalScene({ variant }: { variant: SceneVariant }) {
  const bulbs = BULBS[variant];

  return (
    <>
      <div className={styles.festivalGlow} />
      <div className={styles.wireWrap}>
        <svg className={styles.wire} viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d={`M 0 0 Q 50 ${MAX_DROP * 2} 100 0`} fill="none" />
        </svg>
        <ul className={styles.garland}>
          {bulbs.map(({ hue, style }, index) => (
            <li
              key={index}
              className={`${styles.bulb} ${BULB_HUE_CLASSES[hue]}`}
              style={style}
            />
          ))}
        </ul>
      </div>
    </>
  );
}
